import { RingBuffer } from './ring-buffer'

export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error'

export interface ConsoleEntry {
  level: ConsoleLevel
  message: string
  ts: number
}

const CAPTURED_LEVELS: ConsoleLevel[] = ['log', 'info', 'warn', 'error']

interface Options {
  capacity: number
  now?: () => number
}

export interface ConsoleCapture {
  install(): void
  uninstall(): void
  snapshot(): ConsoleEntry[]
  droppedCount(): number
}

function formatArg(arg: unknown): string {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`
  try {
    return JSON.stringify(arg) ?? String(arg)
  } catch {
    // Circular structures and getters that throw are both common in app
    // objects; a log line is never worth failing a capture over.
    return String(arg)
  }
}

/**
 * Captures recent browser console output into a bounded buffer.
 *
 * The browser has no equivalent of Electron's `console-message` event, so the
 * only way to read back what was logged is to wrap `console` itself. The
 * wrappers always write through to the original methods — a bundle that
 * silenced the console would break debugging in exactly the session the user is
 * reporting on.
 *
 * `window.onerror` and `unhandledrejection` are forwarded in separately because
 * they do not surface through the console stream, and they are usually the
 * entries worth reading.
 */
export function createConsoleCapture(options: Options): ConsoleCapture {
  const { capacity, now = (): number => Date.now() } = options
  const buffer = new RingBuffer<ConsoleEntry>(capacity)
  const originals = new Map<ConsoleLevel, (...args: unknown[]) => void>()
  let installed = false

  const record = (level: ConsoleLevel, message: string): void => {
    buffer.push({ level, message, ts: now() })
  }

  const onError = (event: Event): void => {
    const { message } = event as ErrorEvent
    record('error', `Uncaught: ${message ?? 'unknown error'}`)
  }

  const onRejection = (event: Event): void => {
    const { reason } = event as Event & { reason?: unknown }
    record('error', `Unhandled rejection: ${formatArg(reason)}`)
  }

  return {
    install(): void {
      // Re-entrancy guard: a second install would wrap the wrappers and record
      // every line twice, which reads as a duplicated log rather than a bug.
      if (installed) return
      installed = true

      for (const level of CAPTURED_LEVELS) {
        // eslint-disable-next-line no-console -- reading the original to wrap it
        const original = console[level].bind(console) as (...args: unknown[]) => void
        originals.set(level, original)
        // Installing the interception is this module's whole purpose, and the
        // wrapper always writes through to the original below.
        // eslint-disable-next-line no-console
        console[level] = (...args: unknown[]): void => {
          record(level, args.map(formatArg).join(' '))
          original(...args)
        }
      }

      window.addEventListener('error', onError)
      window.addEventListener('unhandledrejection', onRejection)
    },

    uninstall(): void {
      if (!installed) return
      installed = false

      for (const [level, original] of originals) {
        // eslint-disable-next-line no-console -- restoring what install() wrapped
        console[level] = original
      }
      originals.clear()

      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    },

    snapshot: (): ConsoleEntry[] => buffer.snapshot(),
    droppedCount: (): number => buffer.droppedCount(),
  }
}
